import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
  TablePagination,
} from '@mui/material';
import {
  AccountBalance as BankIcon,
  Refresh as RefreshIcon,
  TrendingDown,
  TrendingUp,
  FileDownload as DownloadIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';
import DashboardLayout from '../../layouts/DashboardLayout';

const API_BASE_URL =
  process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const inr = (paise) => {
  const n = Number(paise || 0) / 100;
  return n.toLocaleString('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  });
};

const PRESETS = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'month', label: 'This month' },
  { id: 'year', label: 'This year' },
  { id: 'custom', label: 'Custom dates' },
];

function Kpi({ title, value, hint, color }) {
  return (
    <Paper sx={{ p: 2, minWidth: 170, flex: 1 }}>
      <Typography variant="caption" color="text.secondary">
        {title}
      </Typography>
      <Typography variant="h6" fontWeight={800} color={color || 'text.primary'}>
        {value}
      </Typography>
      {hint ? (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      ) : null}
    </Paper>
  );
}

const RazorpayLedger = () => {
  const [preset, setPreset] = useState('today');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [source, setSource] = useState('all');
  const [status, setStatus] = useState('all');
  const [settled, setSettled] = useState('all');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_BASE_URL}/razorpay-ledger/admin/overview`, {
        headers: authHeaders(),
        timeout: 120000,
        params: {
          preset,
          from: preset === 'custom' ? from : undefined,
          to: preset === 'custom' ? to : undefined,
          source,
        },
      });
      if (res.data?.success) {
        setData(res.data.data);
        if (res.data.data?.mode === 'db_only' && res.data.data?.setupHint) {
          setError('');
        }
      } else {
        setError(res.data?.message || 'Failed to load Razorpay ledger');
      }
    } catch (err) {
      const msg =
        err.response?.data?.message || err.message || 'Failed to load Razorpay ledger';
      setError(msg);
      if (err.response?.status !== 503) {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  }, [preset, from, to, source]);

  useEffect(() => {
    document.title = 'Razorpay — LOCSY';
  }, []);

  useEffect(() => {
    if (preset === 'custom' && (!from || !to)) return;
    load();
  }, [load, preset, from, to]);

  const payments = useMemo(() => {
    let rows = data?.payments || [];
    if (status !== 'all') {
      rows = rows.filter((r) => r.status === status);
    }
    if (settled === 'yes') rows = rows.filter((r) => r.settled);
    if (settled === 'no') {
      rows = rows.filter((r) => !r.settled && (r.status === 'captured' || r.captured));
    }
    const needle = q.trim().toLowerCase();
    if (needle) {
      rows = rows.filter((r) =>
        [
          r.id,
          r.orderId,
          r.email,
          r.contact,
          r.description,
          r.sourceLabel,
          r.utr,
          r.ownerId,
          r.customerName,
          r.customerLocation,
          r.customerPincode,
          r.customerMobile,
          r.customer?.name,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(needle),
      );
    }
    return rows;
  }, [data, status, settled, q]);

  const summary = data?.summary;
  const sources = data?.sources || [{ key: 'all', label: 'All' }];
  const bySource = summary?.bySource || {};

  const exportCsv = () => {
    const header = [
      'time',
      'payment_id',
      'order_id',
      'source',
      'customer',
      'amount_inr',
      'refund_inr',
      'fee_inr',
      'status',
      'settled',
      'utr',
    ];
    const lines = [header.join(',')];
    payments.forEach((p) => {
      const row = [
        p.createdAt || '',
        p.id,
        p.orderId || '',
        p.sourceLabel || p.source,
        p.customer?.name
          ? [p.customer.name, p.customer.location, p.customer.pincode, p.customer.mobile]
              .filter(Boolean)
              .join(' · ')
          : p.email || p.contact || '',
        ((p.amountPaise || 0) / 100).toFixed(2),
        ((p.amountRefundedPaise || 0) / 100).toFixed(2),
        ((p.feePaise || 0) / 100).toFixed(2),
        p.status,
        p.settled ? 'settled' : 'pending',
        p.utr || '',
      ].map((v) => `"${String(v).replace(/"/g, '""')}"`);
      lines.push(row.join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `loksy-razorpay-${data?.range?.fromYmd || 'from'}-${data?.range?.toYmd || 'to'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const paged = payments.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <DashboardLayout>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center" mb={1}>
          <BankIcon color="primary" />
          <Typography variant="h5" fontWeight={700}>
            Razorpay
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Gateway receipts, refunds and bank settlements for every Loksy product that
          collects money (Mitra, Citizen, ads, wallet, LMS, resume, bills). Razorpay is
          the source of truth; Loksy tags each payment to a feature when possible.
        </Typography>

        <Alert severity="info" sx={{ mb: 2 }}>
          Industry practice (Razorpay / Stripe dashboards): captured amount is{' '}
          <strong>inflow</strong>, refunds + gateway fees are <strong>outflow</strong>,
          net is what should land in the bank after settlement (T+1/T+2 typically).
          UTR appears once Razorpay marks a settlement processed.
        </Alert>

        {error ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {data?.mode === 'db_only' ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>DB-only mode</strong> — Razorpay keys are not set on this server, so
            receipts are loaded from Loksy tables only (wallet, ads, LMS, membership, bills).
            Bank settlement / UTR need gateway keys.
            <Typography component="div" variant="body2" sx={{ mt: 1 }}>
              Add to <code>apps/tsbackend/.env</code> and restart tsbackend:
              <br />
              <code>RAZORPAY_KEY_ID=rzp_live_…</code>
              <br />
              <code>RAZORPAY_KEY_SECRET=…</code>
            </Typography>
          </Alert>
        ) : null}

        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" flexWrap="wrap" gap={1} mb={2}>
            {PRESETS.map((p) => (
              <Chip
                key={p.id}
                label={p.label}
                color={preset === p.id ? 'primary' : 'default'}
                onClick={() => {
                  setPreset(p.id);
                  if (p.id === 'custom' && (!from || !to)) {
                    const today = new Date().toLocaleDateString('en-CA', {
                      timeZone: 'Asia/Kolkata',
                    });
                    const weekAgo = new Date(Date.now() - 6 * 86400000).toLocaleDateString(
                      'en-CA',
                      { timeZone: 'Asia/Kolkata' },
                    );
                    setFrom(from || weekAgo);
                    setTo(to || today);
                  }
                }}
              />
            ))}
            <Box sx={{ flexGrow: 1 }} />
            <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
              Refresh
            </Button>
            <Button startIcon={<DownloadIcon />} onClick={exportCsv} disabled={!payments.length}>
              Export CSV
            </Button>
          </Stack>
          {preset === 'custom' ? (
            <Stack direction="row" spacing={1} mb={2}>
              <TextField
                type="date"
                size="small"
                label="From"
                InputLabelProps={{ shrink: true }}
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
              <TextField
                type="date"
                size="small"
                label="To"
                InputLabelProps={{ shrink: true }}
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </Stack>
          ) : null}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
            <TextField
              select
              size="small"
              label="Source"
              value={source}
              onChange={(e) => {
                setSource(e.target.value);
                setPage(0);
              }}
              sx={{ minWidth: 200 }}
            >
              {sources.map((s) => (
                <MenuItem key={s.key} value={s.key}>
                  {s.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Payment status"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(0);
              }}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="all">All statuses</MenuItem>
              <MenuItem value="captured">Captured</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
              <MenuItem value="refunded">Refunded</MenuItem>
              <MenuItem value="authorized">Authorized</MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="Bank settlement"
              value={settled}
              onChange={(e) => {
                setSettled(e.target.value);
                setPage(0);
              }}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="all">Settled + pending</MenuItem>
              <MenuItem value="yes">Settled to bank</MenuItem>
              <MenuItem value="no">Not yet settled</MenuItem>
            </TextField>
            <TextField
              size="small"
              label="Search"
              placeholder="pay id, email, UTR…"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(0);
              }}
              sx={{ minWidth: 220, flex: 1 }}
            />
          </Stack>
          {data?.range ? (
            <Typography variant="caption" color="text.secondary" display="block" mt={1}>
              {data.range.fromYmd} → {data.range.toYmd}
              {data.keyIdMasked ? ` · key ${data.keyIdMasked}` : ''}
            </Typography>
          ) : null}
        </Paper>

        {data && data.reconAvailable === false ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Settlement recon is not available on this Razorpay key (Settlements API / recon).
            Bank batches may still appear below; per-payment Settled vs Pending needs recon
            enabled on the Razorpay account.
          </Alert>
        ) : null}

        {loading ? (
          <Box py={6} textAlign="center">
            <CircularProgress />
          </Box>
        ) : summary ? (
          <>
            <Stack direction="row" flexWrap="wrap" gap={1.5} mb={2}>
              <Kpi
                title="Inflow (captured)"
                value={inr(summary.inflowPaise)}
                hint={`${summary.countCaptured} receipts`}
                color="success.main"
              />
              <Kpi
                title="Outflow (refunds)"
                value={inr(summary.outflowRefundsPaise)}
                hint="Returned to customers"
              />
              <Kpi
                title="Gateway fees"
                value={inr(summary.outflowFeesPaise)}
                hint="Razorpay fee + GST"
              />
              <Kpi
                title="Net (after fees/refunds)"
                value={inr(summary.netPaise)}
                hint="Expected bank credit"
                color="primary.main"
              />
              <Kpi
                title="Settled to bank"
                value={inr(summary.settledPaise)}
                hint="UTR / processed"
              />
              <Kpi
                title="Pending settlement"
                value={inr(summary.unsettledPaise)}
                hint="Captured, not in bank yet"
              />
              <Kpi
                title="Failed attempts"
                value={inr(summary.failedPaise)}
                hint={`${summary.countFailed || 0} failed`}
              />
            </Stack>

            {Object.keys(bySource).length ? (
              <Stack direction="row" flexWrap="wrap" gap={1} mb={2}>
                {Object.entries(bySource).map(([key, val]) => (
                  <Chip
                    key={key}
                    variant={source === key ? 'filled' : 'outlined'}
                    color={source === key ? 'primary' : 'default'}
                    label={`${sources.find((s) => s.key === key)?.label || key}: ${inr(val.capturedPaise)} (${val.count})`}
                    onClick={() => {
                      setSource(key);
                      setPage(0);
                    }}
                  />
                ))}
              </Stack>
            ) : null}

            <Paper sx={{ px: 2, mb: 2 }}>
              <Tabs value={tab} onChange={(_, v) => setTab(v)}>
                <Tab icon={<TrendingUp />} iconPosition="start" label="Payments / receipts" />
                <Tab icon={<TrendingDown />} iconPosition="start" label="Refunds (outflow)" />
                <Tab icon={<BankIcon />} iconPosition="start" label="Bank settlements" />
              </Tabs>
            </Paper>

            {tab === 0 ? (
              <Paper>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Time (IST)</TableCell>
                      <TableCell>Payment</TableCell>
                      <TableCell>Source</TableCell>
                      <TableCell>Customer</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Bank</TableCell>
                      <TableCell>UTR</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payments.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8}>
                          No Razorpay payments in this range. Historical rows appear once
                          they exist on this Razorpay account (same KEY_ID as live
                          checkout).
                        </TableCell>
                      </TableRow>
                    ) : (
                      paged.map((p) => (
                        <TableRow key={p.id} hover>
                          <TableCell>
                            {p.createdAt
                              ? new Date(p.createdAt).toLocaleString('en-IN')
                              : '—'}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace">
                              {p.id}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {p.method || ''} {p.description ? `· ${p.description}` : ''}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label={p.sourceLabel || p.source} />
                          </TableCell>
                          <TableCell sx={{ minWidth: 180, maxWidth: 280 }}>
                            {p.customer?.name ? (
                              <>
                                <Typography variant="body2" fontWeight={700} lineHeight={1.3}>
                                  {p.customer.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {p.customer.kind === 'mitra' ? 'Mitra shop' : 'Citizen'}
                                </Typography>
                                {p.customer.location ? (
                                  <Typography variant="caption" display="block">
                                    {p.customer.location}
                                  </Typography>
                                ) : null}
                                {p.customer.pincode ? (
                                  <Typography variant="caption" display="block">
                                    PIN {p.customer.pincode}
                                  </Typography>
                                ) : null}
                                {p.customer.mobile ? (
                                  <Typography variant="caption" display="block">
                                    {p.customer.mobile}
                                  </Typography>
                                ) : null}
                              </>
                            ) : (
                              p.email || p.contact || '—'
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {inr(p.amountPaise)}
                            {p.amountRefundedPaise ? (
                              <Typography variant="caption" display="block" color="error">
                                refund {inr(p.amountRefundedPaise)}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              color={
                                p.status === 'captured'
                                  ? 'success'
                                  : p.status === 'failed'
                                    ? 'error'
                                    : 'default'
                              }
                              label={p.status}
                            />
                          </TableCell>
                          <TableCell>
                            {p.status === 'captured' || p.captured ? (
                              p.settled === true ? (
                                <Chip size="small" color="success" label="Settled" />
                              ) : p.settled === false ? (
                                <Chip size="small" color="warning" label="Pending bank" />
                              ) : (
                                <Chip size="small" variant="outlined" label="Unknown" />
                              )
                            ) : (
                              '—'
                            )}
                            {p.dashboardUrl ? (
                              <Button
                                size="small"
                                href={p.dashboardUrl}
                                target="_blank"
                                rel="noreferrer"
                              >
                                Razorpay
                              </Button>
                            ) : null}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                            {p.utr || '—'}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
                <TablePagination
                  component="div"
                  count={payments.length}
                  page={page}
                  onPageChange={(_, p) => setPage(p)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[25, 50, 100]}
                />
              </Paper>
            ) : null}

            {tab === 1 ? (
              <Paper>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Time</TableCell>
                      <TableCell>Refund</TableCell>
                      <TableCell>Payment</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(data.refunds || []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5}>No refunds in this range.</TableCell>
                      </TableRow>
                    ) : (
                      (data.refunds || []).map((r) => (
                        <TableRow key={r.id}>
                          <TableCell>
                            {r.createdAt
                              ? new Date(r.createdAt).toLocaleString('en-IN')
                              : '—'}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{r.id}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>
                            {r.paymentId}
                          </TableCell>
                          <TableCell align="right">{inr(r.amountPaise)}</TableCell>
                          <TableCell>{r.status}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Paper>
            ) : null}

            {tab === 2 ? (
              <Paper>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Time</TableCell>
                      <TableCell>Settlement</TableCell>
                      <TableCell>UTR / bank ref</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell align="right">Fees</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(data.settlements || []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6}>
                          No settlement batches in this range. Razorpay typically
                          settles T+1/T+2. If this stays empty, enable Settlements API
                          on the Razorpay account or check KEY_ID.
                        </TableCell>
                      </TableRow>
                    ) : (
                      (data.settlements || []).map((s) => (
                        <TableRow key={s.id}>
                          <TableCell>
                            {s.settledAt || s.createdAt
                              ? new Date(s.settledAt || s.createdAt).toLocaleString(
                                  'en-IN',
                                )
                              : '—'}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{s.id}</TableCell>
                          <TableCell>{s.utr || '—'}</TableCell>
                          <TableCell align="right">{inr(s.amountPaise)}</TableCell>
                          <TableCell align="right">{inr(s.feesPaise)}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              color={s.status === 'processed' ? 'success' : 'warning'}
                              label={s.status || 'unknown'}
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Paper>
            ) : null}
          </>
        ) : null}
      </Container>
    </DashboardLayout>
  );
};

export default RazorpayLedger;
